// Layout.jsx
import { useState, useMemo, useEffect } from "react";
import Sidebar from "./components/Sidebar/Sidebar";
import TopBar from "./components/Navbar/TopBar";
import MobileSidebar from "./components/Sidebar/MobileSidebar";
import useScreenSize from "../../hooks/useScreenSize";
import FraudNotificationStrip from "../Dashboard/FraudNotificationStrip";
import ProgramInclusionVerificationModal from "./components/InclusionModal/ProgramInclusionVerificationModal";
import { useQuery } from "@tanstack/react-query";
import { getExternalInclusionVerificationStatusService, fetchInstallmentPlanService } from "../../services/users.services";
import { useLocation } from "react-router-dom";

const Layout = ({
  children,
  initialCollapsed = false,
  className = "",
  contentClassName = "",
  sidebarWidths = { expanded: "md:ml-64", collapsed: "md:ml-20" },
  containerBgClass = "bg-background",
  withPadding = true,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Initialize collapsed state based on window width
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 1024 ? true : initialCollapsed;
    }
    return initialCollapsed;
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isMobile } = useScreenSize();
  const location = useLocation();

  const enrolId = localStorage.getItem("enrol_id");
  const programId = localStorage.getItem("program_id");

  const { data: verificationData } = useQuery({
    queryFn: () =>
      getExternalInclusionVerificationStatusService({
        enrol_id: enrolId,
        program_id: programId,
      }),
    queryKey: ["inclusion-verification-status", enrolId, programId],
    enabled: !!enrolId,
    refetchOnWindowFocus: true,
  });

  const { data: installmentPlanData } = useQuery({
    queryFn: () => fetchInstallmentPlanService({ enrol_id: enrolId }),
    queryKey: ["installment-plan-sidebar", enrolId],
    enabled: !!enrolId,
    // staleTime: 5 * 60 * 1000,
  });

  const hasActivePlan = !!installmentPlanData?.data?.plan;
  console.log("installmentPlanData", hasActivePlan);

  const _plan = installmentPlanData?.data?.plan;
  const _agreement = installmentPlanData?.data?.agreement;
  const showAgreementModal = !!(
    _plan?.agreement_file &&
    _agreement?.accepted_online_at == null &&
    _agreement?.verification_status !== "approved"
  );
  const showInclusionModal = useMemo(() => {
    // No enrollment at all — never show the inclusion modal
    if (!enrolId) return false;

    const status = verificationData?.status;
    if (!verificationData) return true;

    if (status === "accepted" || status === "not_applicable") return false;
    return true;
  }, [verificationData, enrolId]);


  const shouldRenderModal =
    showInclusionModal &&
    !location.pathname.includes("my-applications") &&
    !location.search.includes("my-applications");

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 1024) {
        setIsCollapsed(true);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const prefillFreshworksWidget = () => {
      if (typeof window === "undefined" || !window.FreshworksWidget) {
        return;
      }

      window.FreshworksWidget("show", "launcher");

      // 1. Try fetching from sessionStorage (testing mode)
      let user = null;
      try {
        const sessionUser = sessionStorage.getItem("user");
        if (sessionUser) {
          user = JSON.parse(sessionUser);
        }
      } catch (e) {
        console.error("Error parsing user from sessionStorage:", e);
      }

      // 2. Fetch fallbacks from localStorage
      const firstName = localStorage.getItem("username") || "";
      const lastName = localStorage.getItem("lastname") || "";
      const fullName = [firstName, lastName].filter(Boolean).join(" ");
      const email = localStorage.getItem("email") || "";
      const programName = localStorage.getItem("program_name") || "";
      const universityFromProgram = programName.includes(" - ")
        ? programName.split(" - ")[0].trim()
        : "";
      const university = universityFromProgram || localStorage.getItem("university") || "";

      const finalName = user?.name || fullName;
      const finalEmail = user?.email || email;
      const finalUniversity = user?.university || university;
      const finalProgramName = user?.program_name || programName;

      if (finalName || finalEmail) {
        // Identify API to prefill Name and Email
        window.FreshworksWidget("identify", "ticketForm", {
          name: finalName,
          email: finalEmail,
        });

        // Prefill API to populate University and Program Name (Custom fields)
        window.FreshworksWidget("prefill", "ticketForm", {
          custom_fields: {
            cf_institution: finalUniversity,
            cf_course_programme: finalProgramName,
          },
        });

        // Disable editing for the prefilled fields
        window.FreshworksWidget("disable", "ticketForm", [
          "name",
          "email",
          "custom_fields.cf_institution",
          "custom_fields.cf_course_programme",
        ]);
      }
    };

    // Run immediately on load/mount
    prefillFreshworksWidget();

    // Check periodically for up to 10 seconds in case details load async
    let attempts = 0;
    const interval = setInterval(() => {
      const email = localStorage.getItem("email");
      const sessionUser = sessionStorage.getItem("user");

      if (email || sessionUser || attempts > 10) {
        prefillFreshworksWidget();
        clearInterval(interval);
      }
      attempts++;
    }, 1000);

    // Also run on custom enrollmentChanged event
    window.addEventListener("enrollmentChanged", prefillFreshworksWidget);

    return () => {
      clearInterval(interval);
      window.removeEventListener("enrollmentChanged", prefillFreshworksWidget);
    };
  }, []);


  const mainOffsetClass = useMemo(() => {
    if (isMobile) return "";
    return !isCollapsed ? sidebarWidths.expanded : sidebarWidths.collapsed;
  }, [isMobile, isCollapsed, sidebarWidths]);

  const toggleMobileMenu = () => setIsMobileMenuOpen((v) => !v);

  return (
    <>
      {/* {shouldRenderModal && (
        <ProgramInclusionVerificationModal onClose={() => { }} />
      )} */}
      <div className={`min-h-screen ${containerBgClass} ${className}`}>
        <div className="flex">
          {!isMobile && (
            <Sidebar
              isCollapsed={isCollapsed}
              setIsCollapsed={setIsCollapsed}
              showInclusionModal={showInclusionModal}
              hasActivePlan={hasActivePlan}
              showAgreementModal={showAgreementModal}
            />
          )}

          {isMobile && (
            <MobileSidebar
              isOpen={isMobileMenuOpen}
              onClose={() => setIsMobileMenuOpen(false)}
              showInclusionModal={showInclusionModal}
              hasActivePlan={hasActivePlan}
              showAgreementModal={showAgreementModal}
            />
          )}

          <div
            className={`flex-1 min-w-0 ml-0 ${mainOffsetClass} transition-all duration-300`}
          >
            <TopBar isMobile={isMobile} onMobileMenuToggle={toggleMobileMenu} />
            <div
              className={`${withPadding ? "p-4 md:p-8 pt-6" : ""
                } ${contentClassName}`}
            >
              {children}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Layout;
